import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ThemeToggle, ThemeToggleIcon } from '@/components/ThemeToggle';
import { useSidebar } from '@/context/SidebarContext';
import { useTradingMode } from '@/context/TradingModeContext';
import {
  BarChart3, Wallet, Building2, Layers, ArrowUpDown, LineChart, Bell,
  Bot, Grid3X3, Target, Zap, TestTube, ArrowRightLeft, Wand2, PieChart,
  MessageCircle, BookOpen, Activity, Brain, Cpu, Server, GraduationCap,
  Rocket, Clock, Globe, Shield, Gift, FileText, Crown, Briefcase,
  TrendingUp, ChevronRight, ChevronLeft, Users, Send, RefreshCw, Waves, Key, Trophy, Radio, FlaskConical, Crosshair, Timer
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Core',
    items: [
      { icon: BarChart3, label: 'Dashboard', href: '/dashboard' },
      { icon: Timer, label: 'Open Positions', href: '/open-positions', badge: 'LIVE' },
      { icon: Activity, label: 'Market Intel', href: '/market-intelligence', badge: 'MCAS' },
      { icon: Wallet, label: 'Portfolio', href: '/portfolio' },
      { icon: ArrowUpDown, label: 'Trade', href: '/trading' },
      { icon: LineChart, label: 'Charts', href: '/charts', badge: 'TV' },
    ]
  },
  {
    label: 'Bots & Automation',
    items: [
      { icon: Bot, label: 'Trading Bots', href: '/bots', badge: 'ALL' },
      { icon: Radio, label: 'Signal Bridge', href: '/signal-bridge', badge: 'LIVE' },
      { icon: Zap, label: 'SmartTrades', href: '/smart-trades' },
      { icon: Zap, label: 'Power Bot Suite', href: '/power-bot-suite', badge: 'POWER' },
      { icon: Brain, label: 'RL Optimizer', href: '/rl-optimizer', badge: 'AI' },
      { icon: Users, label: 'Copy Trading', href: '/copy-trading' },
      { icon: Trophy, label: 'Leaderboard', href: '/leaderboard', badge: 'HOT' },
    ]
  },
  {
    label: 'Markets',
    items: [
      { icon: Building2, label: 'Stocks', href: '/stock-trading', badge: 'LIVE' },
      { icon: Layers, label: 'Options', href: '/options-trading' },
      { icon: Zap, label: 'Live Trading', href: '/live-trading', badge: 'LIVE' },
      { icon: Bell, label: 'Big Movers', href: '/alerts', badge: 'HOT' },
      { icon: Waves, label: 'Order Book', href: '/orderbook-whales' },
    ]
  },
  {
    label: 'Pro Tools',
    items: [
      { icon: TestTube, label: 'Paper Trading', href: '/paper-trading', badge: 'PRO' },
      { icon: ArrowRightLeft, label: 'Arbitrage', href: '/arbitrage', badge: 'PRO' },
      { icon: Wand2, label: 'AI Strategy', href: '/ai-strategy', badge: 'PRO' },
      { icon: PieChart, label: 'Analytics+', href: '/advanced-analytics', badge: 'PRO' },
      { icon: Brain, label: 'AI Signals', href: '/ai-signals', badge: 'PRO' },
      { icon: Cpu, label: 'Advanced AI', href: '/advanced-ai' },
      { icon: LineChart, label: 'Backtesting', href: '/backtesting' },
    ]
  },
  {
    label: 'Analytics',
    items: [
      { icon: BarChart3, label: 'Performance', href: '/performance' },
      { icon: Activity, label: 'Trade Activity', href: '/trade-activity', badge: 'LIVE' },
      { icon: Zap, label: 'Swing Activity', href: '/swing-activity', badge: 'LIVE' },
      { icon: Shield, label: 'Trade Quality', href: '/trade-quality', badge: 'NEW' },
      { icon: FlaskConical, label: 'Backtest', href: '/backtest', badge: 'NEW' },
      { icon: Crosshair, label: 'Optimizer', href: '/strategy-optimizer', badge: 'NEW' },
      { icon: Target, label: 'Strategy Stats', href: '/strategy-performance' },
      { icon: BookOpen, label: 'Journal', href: '/journal' },
      { icon: Shield, label: 'Risk', href: '/analytics' },
      { icon: FileText, label: 'Tax Reports', href: '/tax-reporting', badge: 'PRO' },
    ]
  },
  {
    label: 'Settings & More',
    items: [
      { icon: Key, label: 'Exchange Keys', href: '/exchange-keys' },
      { icon: MessageCircle, label: 'Alert Channels', href: '/alert-channels', badge: 'PRO' },
      { icon: Send, label: 'Alerts Bot', href: '/notification-bots' },
      { icon: Activity, label: 'TradingView', href: '/tradingview-alerts', badge: 'TV' },
      { icon: RefreshCw, label: 'Rebalance', href: '/rebalancing' },
      { icon: Clock, label: 'Econ Calendar', href: '/calendar' },
      { icon: Globe, label: 'Marketplace', href: '/marketplace' },
      { icon: GraduationCap, label: 'Learn Trading', href: '/beginners-guide' },
      { icon: Building2, label: 'Multi-Account', href: '/multi-account', badge: 'PRO' },
      { icon: Gift, label: 'Referrals', href: '/referrals', badge: 'EARN' },
      { icon: Server, label: 'Task Monitor', href: '/task-monitor' },
      { icon: Activity, label: 'Market Health', href: '/market-health' },
    ]
  },
];

const BADGE_COLORS = {
  ADMIN: 'bg-amber-500 text-white',
  LIVE: 'bg-emerald-500 text-white',
  HOT: 'bg-red-500 text-white',
  MCAS: 'bg-cyan-500 text-white',
  POWER: 'bg-orange-500 text-white',
  'ADD-ON': 'bg-purple-500 text-white',
  AGENT: 'bg-teal-500 text-white',
  EARN: 'bg-emerald-500 text-white',
  AI: 'bg-violet-500 text-white',
};

function useSafeTradingMode() {
  try { return useTradingMode(); } catch { return { mode: 'paper', isLive: false, exchangeName: null }; }
}

export default function AppSidebar({ user }) {
  const location = useLocation();
  const { collapsed, setCollapsed, toggle } = useSidebar();
  const tradingMode = useSafeTradingMode();

  const extraItems = [];
  if (user?.is_admin) {
    extraItems.push({ icon: Crown, label: 'Admin Panel', href: '/admin', badge: 'ADMIN' });
  }
  if (user?.is_agent) {
    extraItems.push({ icon: Briefcase, label: 'Agent Portal', href: '/agent-dashboard', badge: 'AGENT' });
  }

  return (
    <>
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={() => setCollapsed(true)}
        />
      )}
      <div
        data-testid="app-sidebar"
        className={`fixed left-0 top-0 h-full bg-slate-900/95 backdrop-blur-xl border-r border-white/10 z-40 transition-all duration-300 flex flex-col ${
          collapsed ? '-translate-x-full md:translate-x-0 md:w-16' : 'translate-x-0 w-64 md:w-56 lg:w-64'
        }`}
      >
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-white/10 flex-shrink-0">
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <TrendingUp className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-display text-base font-bold text-white">BitTrade <span className="text-cyan-400">HQ</span></span>
            </Link>
          )}
          <Button
            data-testid="sidebar-toggle"
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Trading Mode Indicator */}
        <Link to="/dashboard" className="flex-shrink-0">
          <div
            className={`mx-2 mt-2 px-2 py-1.5 rounded-lg border text-center transition-all ${
              tradingMode.isLive
                ? 'bg-red-500/10 border-red-500/25 hover:bg-red-500/15'
                : 'bg-sky-500/8 border-sky-500/15 hover:bg-sky-500/12'
            }`}
            data-testid="sidebar-trading-mode"
          >
            {collapsed ? (
              <div className={`w-2 h-2 rounded-full mx-auto ${tradingMode.isLive ? 'bg-red-400 animate-pulse' : 'bg-sky-400'}`} />
            ) : (
              <div className="flex items-center justify-center gap-1.5">
                {tradingMode.isLive ? (
                  <Zap className="w-3 h-3 text-red-400" />
                ) : (
                  <Shield className="w-3 h-3 text-sky-400" />
                )}
                <span className={`text-[10px] font-bold uppercase tracking-wider ${
                  tradingMode.isLive ? 'text-red-400' : 'text-sky-400'
                }`}>
                  {tradingMode.isLive ? `LIVE ${tradingMode.exchangeName ? '· ' + tradingMode.exchangeName : ''}` : 'PAPER'}
                </span>
              </div>
            )}
          </div>
        </Link>

        {/* Nav Sections */}
        <nav className="flex-1 px-2 pt-2 pb-1 overflow-y-auto scrollbar-thin">
          {NAV_SECTIONS.map((section, si) => (
            <div key={si} className="mb-1">
              {!collapsed && (
                <div className="px-3 pt-2 pb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {section.label}
                  </span>
                </div>
              )}
              {collapsed && si > 0 && (
                <div className="mx-2 my-1 border-t border-white/5" />
              )}
              {section.items.map((item, i) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link key={i} to={item.href}>
                    <div
                      data-testid={`nav-${item.href.replace(/\//g, '-').slice(1)}`}
                      className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="text-sm font-medium truncate">{item.label}</span>
                          {item.badge && (
                            <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded-sm flex-shrink-0 ${
                              BADGE_COLORS[item.badge] || 'bg-blue-500 text-white'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ))}

          {/* Admin/Agent items */}
          {extraItems.length > 0 && (
            <div className="mb-1">
              {!collapsed && (
                <div className="px-3 pt-2 pb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Admin</span>
                </div>
              )}
              {extraItems.map((item, i) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link key={i} to={item.href}>
                    <div
                      data-testid={`nav-${item.href.replace(/\//g, '-').slice(1)}`}
                      className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all ${
                        isActive
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                          : 'text-amber-400 hover:bg-amber-500/10'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="text-sm font-medium truncate">{item.label}</span>
                          {item.badge && (
                            <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded-sm flex-shrink-0 ${
                              BADGE_COLORS[item.badge] || 'bg-blue-500 text-white'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        {/* Theme Toggle */}
        {!collapsed && (
          <div className="flex-shrink-0 px-3 pt-2">
            <ThemeToggle className="w-full justify-start text-slate-400 hover:text-white" />
          </div>
        )}
        {collapsed && (
          <div className="flex-shrink-0 px-2 pt-2 flex justify-center">
            <ThemeToggleIcon />
          </div>
        )}

        {/* Upgrade CTA */}
        {!collapsed && (
          <div className="flex-shrink-0 p-3 border-t border-white/10">
            <div className="p-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-blue-400">Upgrade to Pro</span>
              </div>
              <p className="text-[10px] text-slate-400 mb-2">Get unlimited bots & AI signals</p>
              <Link to="/pricing">
                <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs h-7">
                  View Plans
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export { useSidebar } from '@/context/SidebarContext';
